var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createActionsInstance, getAdditionalHeaders } from "./actions.mjs";
import { orderIdQueryParam, paymentErrorQueryParam, showOrderConfirmationQueryParam } from "./constants.mjs";
import { getWithRetries, HttpError, postWithRetries } from "./http.mjs";
import { paymentMethodLogosResponseSchema } from "./cart/payment.mjs";
import { createStateInstance } from "./state.mjs";
import { objectEntries } from "./utils.mjs";
import { customStickerResponseSchema } from "./customSticker/index.mjs";
import { orderResponseSchema } from "./order.mjs";
import { customStickerCache } from "./customSticker/cache.mjs";
import { initializeCheckoutElements } from "./elements/index.mjs";
import { countriesResponseSchema } from "./shipping.mjs";
/**
 * Creates an instance of the checkout SDK for a certain cart. Note that it's possible to have multiple
 * instances of the checkout SDK on the same page, but they should not share the same cart.
 * If you use multiple instances you need to make sure that your onChange callbacks are scoped to the
 * instance you're interested in.
 */
export const createCheckoutInstance = (args) => {
    var _a, _b;
    const state = createStateInstance(Object.assign(Object.assign({}, args), { onRedirect: (_a = args.onRedirect) !== null && _a !== void 0 ? _a : defaultOnRedirect, onPostForm: (_b = args.onPostForm) !== null && _b !== void 0 ? _b : defaultOnPostForm }));
    // This just makes things easier for debugging so that we can access the state from the console.
    // It shouldn't be used for anything else.
    window.__stickerAppCheckoutState = state;
    const actions = createActionsInstance(state);
    if (typeof window !== 'undefined') {
        const currentUrl = new URL(window.location.href);
        if (currentUrl.searchParams.has(showOrderConfirmationQueryParam) && currentUrl.searchParams.has(orderIdQueryParam)) {
            // setTimeout is used to ensure that we've returned the instance before we ask the caller to show
            // the order confirmation page. This is to avoid any potential race condition where the called tries
            // to call the instance before it's returned.
            setTimeout(() => {
                actions.clearCartAndShowOrderConfirmation(currentUrl.searchParams.get(orderIdQueryParam));
            }, 0);
        }
        if (currentUrl.searchParams.has(paymentErrorQueryParam)) {
            args.onUnexpectedError(currentUrl.searchParams.get(paymentErrorQueryParam));
        }
    }
    let paymentMethodLogosResponse;
    const saveCustomSticker = (customSticker, id) => __awaiter(void 0, void 0, void 0, function* () {
        const response = yield postWithRetries('/custom-stickers' + (id ? '/' + id : ''), state.checkoutApiBaseUrl, { customSticker }, customStickerResponseSchema, state.tokenString, getAdditionalHeaders(state));
        return response.customSticker;
    });
    initializeCheckoutElements(state, actions);
    return {
        state,
        actions,
        createCustomSticker: (customSticker) => __awaiter(void 0, void 0, void 0, function* () {
            const savedCustomSticker = yield saveCustomSticker(customSticker, undefined);
            customStickerCache.set(savedCustomSticker);
            return savedCustomSticker;
        }),
        updateCustomSticker: (customSticker) => __awaiter(void 0, void 0, void 0, function* () {
            const savedCustomSticker = yield saveCustomSticker(customSticker, customSticker.id);
            // Since updating a custom sticker can change the cart, we need to reload the cart
            yield actions.reload();
            customStickerCache.set(savedCustomSticker);
            return savedCustomSticker;
        }),
        getCustomSticker: (customStickerId, customStickerVersion) => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            if (customStickerVersion !== undefined) {
                const cachedCustomSticker = yield customStickerCache.get(customStickerId, customStickerVersion !== null && customStickerVersion !== void 0 ? customStickerVersion : '');
                if (cachedCustomSticker) {
                    return cachedCustomSticker;
                }
            }
            const customSticker = (_a = (yield makeGetRequest('/custom-stickers/' + customStickerId, state, customStickerResponseSchema))) === null || _a === void 0 ? void 0 : _a.customSticker;
            if (customSticker) {
                customStickerCache.set(customSticker);
            }
            return customSticker;
        }),
        getOrder: (orderId) => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            return (_a = (yield makeGetRequest('/orders/' + orderId, state, orderResponseSchema))) === null || _a === void 0 ? void 0 : _a.order;
        }),
        paymentLogos: () => __awaiter(void 0, void 0, void 0, function* () {
            if (!paymentMethodLogosResponse) {
                paymentMethodLogosResponse = yield getWithRetries('/carts/payment-method-logos', state.checkoutApiBaseUrl, paymentMethodLogosResponseSchema, state.tokenString, getAdditionalHeaders(state));
            }
            return paymentMethodLogosResponse;
        }),
        countries: () => __awaiter(void 0, void 0, void 0, function* () {
            return yield getWithRetries('/shipping/countries', state.checkoutApiBaseUrl, countriesResponseSchema, state.tokenString, getAdditionalHeaders(state));
        }),
        updateCountry: (countryCode, currency) => __awaiter(void 0, void 0, void 0, function* () {
            if (state.countryCode !== countryCode || state.currency !== currency) {
                state.update({ countryCode, currency });
                yield actions.reload();
            }
        }),
        updateTokenString: (tokenString) => __awaiter(void 0, void 0, void 0, function* () {
            if (state.tokenString !== tokenString) {
                state.update({ tokenString });
                yield actions.reload();
            }
        }),
    };
};
const makeGetRequest = (path, state, schema) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        return yield getWithRetries(path, state.checkoutApiBaseUrl, schema, state.tokenString, getAdditionalHeaders(state));
    }
    catch (error) {
        if (error instanceof HttpError && error.status === 404) {
            return undefined;
        }
        throw error;
    }
});
export const defaultOnRedirect = (url) => {
    window.location.href = url;
};
export const defaultOnPostForm = (url, data, formEncoding) => {
    const form = document.createElement('form');
    form.acceptCharset = formEncoding || 'utf-8';
    form.style.display = 'none';
    form.method = 'post';
    form.action = url;
    objectEntries(data).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.value = value;
        input.name = key;
        form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
};
//# sourceMappingURL=instance.js.map